import React, {useContext, useState, useMemo, useEffect} from 'react';
import {AuthContext} from "../../auth/providers/AuthProvider";
import {useMutation, useQuery, useQueryClient} from "react-query";
import { addNewTransaction, listTemplateTransactions, verifyTransaction, fetchTransactionsPage } from "../../../services/template/Transactions";
import { listTemplateStatements } from "../../../services/template/Statements";
import FormatDate from "../../../utils/formatters/FormatDate";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeContext } from 'styled-components';
import {CsrfContext} from "../../../providers/CsrfProvider";
import {TemplateContext} from "../../../providers/TemplateProvider";
import { Box, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, IconButton, useMediaQuery, useTheme, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApiClient from "../../../services/ApiClient";
import NewUUID from "../../../utils/randy/NewUUID";


const PAYMENT_NOTE = "ezsplit";
const VENMO_PROVIDER = "venmo";

const fetchPaymentOptions = async (csrfToken, templateId) => {
    const { data } = await ApiClient.get(`/api/banking/templates/${templateId}`, {
        headers: {
            accept: "text/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        }
    });
    return data.response;
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(Math.abs(amount));
};

const buildVenmoUrl = (venmoId, amount) => {
    const cleanedVenmoId = venmoId.replace(/^@/, '');
    const encodedAmount = encodeURIComponent(Math.abs(amount));
    const encodedNote = encodeURIComponent(PAYMENT_NOTE);
    return `https://venmo.com/?txn=pay&recipients=${cleanedVenmoId}&amount=${encodedAmount}&note=${encodedNote}`;
};

const TransactionFormRow = ({ transaction, templateId, isOwner, statements, transactions, onClose, onSuccess }) => {
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const theme = useContext(ThemeContext);
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({ amount: 0 });
    const [amountError, setAmountError] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [selectedStatementId, setSelectedStatementId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [venmoUrl, setVenmoUrl] = useState(null);

    // Check if this is a participant viewing their own payment transaction
    const isParticipantViewingOwnPayment = !isOwner && transaction && transaction.amount > 0 && transaction.user?.sub && authClaims?.user_sub && transaction.user.sub.toUpperCase() === authClaims.user_sub.toUpperCase();

    // Calculate unpaid balance for each statement for the current user
    const statementsWithBalance = useMemo(() => {
        if (!statements || !transactions || !transaction?.user?.sub) {
            return [];
        }

        return statements.map(statement => {
            // Find all transactions for this statement and this user
            const userTransactions = (transactions || []).filter(t =>
                t.statement_id === statement.id &&
                t.user?.sub &&
                t.user.sub.toUpperCase() === transaction.user.sub.toUpperCase()
            );

            // Calculate total paid (positive transactions that are verified)
            const totalPaid = userTransactions
                .filter(t => t.amount > 0 && t.verified_at)
                .reduce((sum, t) => sum + t.amount, 0);

            // Calculate total owed (negative transactions)
            const totalOwed = userTransactions
                .filter(t => t.amount < 0)
                .reduce((sum, t) => sum + Math.abs(t.amount), 0);

            const unpaidBalance = totalOwed - totalPaid;

            return {
                ...statement,
                unpaidBalance,
                totalOwed,
                totalPaid
            };
        }).filter(s => s.unpaidBalance > 0) // Only show statements with unpaid balance
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // Sort by oldest first
    }, [statements, transactions, transaction?.user?.sub]);

    useEffect(() => {
        if (statementsWithBalance && statementsWithBalance.length > 0) {
            setSelectedStatementId(statementsWithBalance[0].id);
        } else {
            setSelectedStatementId(null);
        }
    }, [statementsWithBalance]);

    const { data: paymentOptions, isLoading: isLoadingPaymentOptions, isError: isPaymentOptionsError } = useQuery(
        ['template_payment_options', transaction.bill_id],
        () => fetchPaymentOptions(csrfToken, transaction.bill_id),
        {
            enabled: !isOwner && !isParticipantViewingOwnPayment
        }
    );

    const mutation = useMutation(addNewTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']);
            if (onSuccess) {
                onSuccess();
            }
        },
    });

    const confirmPaymentMutation = useMutation(addNewTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']);
            setConfirmDialogOpen(false);
            setVenmoUrl(null);
            if (onSuccess) {
                onSuccess();
            }
        },
    });

    const verifyTransactionMutation = useMutation(verifyTransaction(csrfToken), {
        onSuccess: () => {
            queryClient.invalidateQueries(['transactions', String(templateId)]);
            queryClient.invalidateQueries(['templates']);
            if (onSuccess) {
                onSuccess();
            }
        },
    });

    const handleVerifyTransaction = (verified) => {
        if (transaction?.id && transaction?.statement_id && templateId) {
            verifyTransactionMutation.mutate({
                statementId: transaction.statement_id,
                transactionId: transaction.id,
                templateId: templateId,
                verified: verified
            });
        }
    };

    const handleVenmoClick = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
        setVenmoUrl(url);
        setConfirmDialogOpen(true);
    };

    const handleConfirmPayment = () => {
        const paymentAmount = Math.abs(transaction.amount);
        if (templateId && transaction.statement_id && transaction.user?.sub) {
            confirmPaymentMutation.mutate({
                amount: paymentAmount,
                user_sub: transaction.user.sub,
                statement_id: transaction.statement_id,
                templateId: templateId
            });
        }
        setConfirmDialogOpen(false);
        setVenmoUrl(null);
    };

    const handleCancelPayment = () => {
        setConfirmDialogOpen(false);
        setVenmoUrl(null);
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        let parsedValue = value;
        if (e.target.type === 'number') {
            parsedValue = parseFloat(value);
            if (isNaN(parsedValue)) {
                parsedValue = 0;
            }
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: parsedValue,
        }));
        if (submitAttempted && amountError) {
            const num = parseFloat(value);
            if (!isNaN(num) && num > 0) {
                setAmountError(false);
            }
        }
    };

    const validateAmount = (amount) => {
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0;
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        setSubmitAttempted(true);
        const isValid = validateAmount(formData.amount);

        if (!isValid) {
            setAmountError(true);
            return;
        }

        const statementIdToUse = selectedStatementId ||
            (statementsWithBalance && statementsWithBalance.length > 0 ? statementsWithBalance[0].id : transaction.statement_id);

        setAmountError(false);
        const transactionData = {
            ...formData,
            user_sub: transaction.user.sub,
            statement_id: statementIdToUse,
            templateId: templateId
        };
        mutation.mutate(transactionData);
    };

    const isValid = () => {
        return validateAmount(formData.amount);
    };

    return (
        <>
            {/* Header Row */}
            <TableRow>
                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }}></TableCell>
                <TableCell colSpan={3} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                    <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                        {isOwner && transaction.amount < 0 ? 'Record Payment' : 'Payment'}
                    </Typography>
                </TableCell>
                <TableCell sx={{
                    width: '56px !important',
                    minWidth: '56px !important',
                    maxWidth: '56px !important',
                    p: 0,
                    flexShrink: 0,
                    flexGrow: 0,
                    borderBottom: 'none',
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%'
                    }}>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                color: 'inherit',
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: theme.colors.overlayHover,
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>

            {/* Form Row */}
            <TableRow>
                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                <TableCell colSpan={3} sx={{ pl: 0, pr: 0, py: 2 }}>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        {isOwner ? (
                            <>
                                {/* Owner: Record Payment Form */}
                                {transaction.amount < 0 && statementsWithBalance && statementsWithBalance.length > 0 && (
                                    <>
                                        <Box sx={{ p: 2, borderRadius: 1, bgcolor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)' }}>
                                            <Typography variant="body2" sx={{ color: theme.colors.text, mb: 0.5 }}>
                                                Balance
                                            </Typography>
                                            <Typography variant="h5" sx={{ color: theme.colors.error || '#f44336', fontWeight: 600 }}>
                                                ${statementsWithBalance.reduce((sum, s) => sum + s.unpaidBalance, 0).toFixed(2)}
                                            </Typography>
                                        </Box>
                                        {statementsWithBalance.length > 1 && (
                                            <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 1 }}>
                                                <Stack spacing={1}>
                                                    {statementsWithBalance.map((statement) => (
                                                        <Box
                                                            key={statement.id}
                                                            onClick={() => setSelectedStatementId(statement.id)}
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 1,
                                                                border: `2px solid ${selectedStatementId === statement.id ? theme.colors.secondary : theme.colors.text}`,
                                                                cursor: 'pointer',
                                                                bgcolor: selectedStatementId === statement.id ? (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)') : 'transparent',
                                                                '&:hover': {
                                                                    bgcolor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                },
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ color: theme.colors.text, fontWeight: 500 }}>
                                                                        {FormatDate(statement.created_at)}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: theme.colors.text, opacity: 0.7 }}>
                                                                        Total: ${statement.cost?.toFixed(2)}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body1" sx={{ color: theme.colors.error || '#f44336', fontWeight: 600 }}>
                                                                    ${statement.unpaidBalance.toFixed(2)} due
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </>
                                )}
                                {transaction.amount > 0 && transaction.id ? (
                                    <Stack spacing={2}>
                                        <Button
                                            onClick={() => handleVerifyTransaction(true)}
                                            disabled={verifyTransactionMutation.isLoading}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                                backgroundColor: 'transparent',
                                                color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                '&:hover': {
                                                    borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                                    backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            {verifyTransactionMutation.isLoading ? 'Processing...' : 'Confirm Payment'}
                                        </Button>
                                        {!transaction.denied_at && (
                                            <Button
                                                onClick={() => handleVerifyTransaction(false)}
                                                disabled={verifyTransactionMutation.isLoading}
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    border: `1px solid ${theme.colors.error || '#f44336'}`,
                                                    backgroundColor: 'transparent',
                                                    color: theme.colors.error || '#f44336',
                                                    '&:hover': {
                                                        borderColor: theme.colors.error || '#f44336',
                                                        backgroundColor: `${theme.colors.error || '#f44336'}10`,
                                                    },
                                                }}
                                            >
                                                {verifyTransactionMutation.isLoading ? 'Processing...' : 'Deny Payment'}
                                            </Button>
                                        )}
                                    </Stack>
                                ) : (
                                    <>
                                        <TextField
                                            label="Amount"
                                            type="number"
                                            name="amount"
                                            value={formData.amount || ''}
                                            onChange={onChange}
                                            fullWidth
                                            inputProps={{ min: 0, step: 0.01 }}
                                            error={submitAttempted && amountError}
                                            helperText={submitAttempted && amountError ? "Please enter a valid amount greater than 0" : ""}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.colors.text,
                                                    fontSize: '16px',
                                                    '& fieldset': {
                                                        borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.colors.text,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.colors.text,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.colors.secondary || theme.colors.brandSecondary,
                                                    fontSize: '16px',
                                                    '&.Mui-focused': {
                                                        color: theme.colors.text,
                                                    },
                                                },
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                            <Button
                                                type={isValid() ? "submit" : "button"}
                                                onClick={isValid() ? undefined : onClose}
                                                variant="outlined"
                                                fullWidth
                                                disabled={mutation.isLoading}
                                                sx={{
                                                    border: `1px solid ${isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc')}`,
                                                    backgroundColor: 'transparent',
                                                    color: isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : theme.colors.text,
                                                    '&:hover': {
                                                        borderColor: isValid() ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc'),
                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                    },
                                                }}
                                            >
                                                {mutation.isLoading ? 'Processing...' : (isValid() ? 'Record Payment' : 'Cancel')}
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Non-Owner: Payment Options */}
                                {isParticipantViewingOwnPayment ? (
                                    <Alert severity={transaction.verified_at ? "success" : transaction.denied_at ? "error" : "warning"}>
                                        <Typography variant="body2" sx={{ color: theme.colors.text }}>
                                            {transaction.verified_at 
                                                ? `Payment verified on ${FormatDate(transaction.verified_at)}`
                                                : transaction.denied_at
                                                    ? `Payment denied on ${FormatDate(transaction.denied_at)}`
                                                    : 'Payment pending verification by bill owner'}
                                        </Typography>
                                    </Alert>
                                ) : (
                                    <>
                                        {isLoadingPaymentOptions ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress size={32} sx={{ mr: 2 }} />
                                                <Typography variant="body1" color={theme.colors.text}>
                                                    Loading payment options...
                                                </Typography>
                                            </Box>
                                        ) : isPaymentOptionsError ? (
                                            <Alert severity="error">
                                                Failed to load payment options. Please try again.
                                            </Alert>
                                        ) : !paymentOptions || !Array.isArray(paymentOptions) || paymentOptions.length === 0 ? (
                                            <Alert severity="info">
                                                <Typography variant="body2">
                                                    Ask the bill owner to configure payment methods
                                                </Typography>
                                            </Alert>
                                        ) : (
                                            <Stack spacing={2}>
                                                {paymentOptions.map((paymentOption, index) => {
                                                    const isVenmo = paymentOption.provider.toLowerCase() === VENMO_PROVIDER;
                                                    const url = isVenmo && paymentOption.venmo_id 
                                                        ? buildVenmoUrl(paymentOption.venmo_id, transaction.amount)
                                                        : null;
                                                    
                                                    return (
                                                        <Button
                                                            key={`payment-option-${index}-${paymentOption.provider}`}
                                                            variant="contained"
                                                            color="primary"
                                                            fullWidth
                                                            size="large"
                                                            onClick={() => {
                                                                if (url) {
                                                                    handleVenmoClick(url);
                                                                }
                                                            }}
                                                            sx={{ textTransform: 'none', py: 1.5 }}
                                                        >
                                                            Pay {formatCurrency(transaction.amount)} with {paymentOption.provider}
                                                        </Button>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                    <Button
                                        type="button"
                                        onClick={onClose}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.text,
                                            '&:hover': {
                                                borderColor: theme.colors.border || '#ccc',
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </TableCell>
                <TableCell sx={{
                    width: '56px !important',
                    minWidth: '56px !important',
                    maxWidth: '56px !important',
                    p: 0,
                    flexShrink: 0,
                    flexGrow: 0,
                }}></TableCell>
            </TableRow>

            {/* Payment Confirmation Dialog */}
            {confirmDialogOpen && (
                <TableRow>
                    <TableCell colSpan={5} sx={{ p: 2 }}>
                        <Box sx={{ p: 2, borderRadius: 1, bgcolor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)' }}>
                            <Typography variant="h6" sx={{ color: theme.colors.text, mb: 2, fontWeight: 600 }}>
                                Confirm Payment
                            </Typography>
                            <Typography variant="body1" sx={{ color: theme.colors.text, mb: 2 }}>
                                Did you make a payment of {formatCurrency(transaction.amount)} to {paymentOptions?.find(p => p.provider.toLowerCase() === VENMO_PROVIDER)?.venmo_id ? `@${paymentOptions.find(p => p.provider.toLowerCase() === VENMO_PROVIDER).venmo_id.replace(/^@/, '')}` : 'this person'}?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                <Button
                                    onClick={handleConfirmPayment}
                                    variant="contained"
                                    fullWidth
                                    disabled={confirmPaymentMutation.isLoading}
                                    sx={{
                                        backgroundColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                        color: theme.colors.buttonText || '#fff',
                                        '&:hover': {
                                            backgroundColor: theme.colors.primaryHover || theme.colors.secondary,
                                        },
                                    }}
                                >
                                    {confirmPaymentMutation.isLoading ? 'Processing...' : 'Yes, I paid'}
                                </Button>
                                <Button
                                    onClick={handleCancelPayment}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        border: `1px solid ${theme.colors.border || '#ccc'}`,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.text,
                                        '&:hover': {
                                            borderColor: theme.colors.border || '#ccc',
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    No, Cancel
                                </Button>
                            </Box>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const TransactionsModel = ({ templateId, enabled = true, hydrating = false, onHydrated, expandedView = false, headerAction }) => {
    const theme = useContext(ThemeContext);
    const mediaTheme = useTheme();
    // Use compact format (0) on extra small screens, full format (1) on sm and up
    const isExtraSmall = useMediaQuery(mediaTheme.breakpoints.down('sm'));
    const dateFormatLevel = isExtraSmall ? 0 : 1;
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const { ownerSub } = useContext(TemplateContext);

    const [editingTransactionId, setEditingTransactionId] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuTransactionId, setMenuTransactionId] = useState(null);

    const handleMenuClick = (event, transactionId) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuTransactionId(transactionId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuTransactionId(null);
    };

    const handlePaymentClick = (transaction) => {
        handleMenuClose();
        setEditingTransactionId(transaction.id);
    };


    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: theme.colors.primary,
        border: `2px solid ${theme.colors.borderDark}`,
        boxShadow: 24,
        p: 4,
    };



    const onChange = (e) => {
        const { name, value } = e.target;
        let parsedValue = value;
        // Check if the input type is number and convert the value
        if (e.target.type === 'number') {
            parsedValue = parseFloat(value);
            // Check if the parsedValue is NaN (Not a Number), then revert it to an empty string or zero
            if (isNaN(parsedValue)) {
                parsedValue = 0;
            }
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: parsedValue,
        }));
    };

    // Mutation to manage data that updates
    const queryClient = useQueryClient();
    const mutation = useMutation(addNewTransaction(csrfToken), {
        onSuccess: () => {
            // Invalidate and refetch the 'users' query after adding a new user
            queryClient.invalidateQueries(['transactions', String(templateId)]);
        },
    });

    const onSubmit = () => {
        if (formData.amount > 0) {
            formData.user_sub=currentItem.user.sub
            formData.statement_id=currentItem.statement_id
            handleAddTransaction(formData); // Combine existing and new users
            setFormData({ amount: 0}); // Reset the form data
        }
    };

    const handleAddTransaction = (formData) => {
        formData.templateId=templateId;
        mutation.mutate(formData);
    };

    const [accumulatedTx, setAccumulatedTx] = useState([]);
    const [nextTxCursor, setNextTxCursor] = useState('');
    const [loadingMoreTx, setLoadingMoreTx] = useState(false);

    // Query for first page of transactions (cursor-based). Only fetch when pane is in view (enabled).
    const { data: transactionsData, isLoading: isQueryLoading, isError: isQueryError, refetch } = useQuery(
        ['transactions', String(templateId), { cursor: '' }],
        listTemplateTransactions(csrfToken),
        {
            enabled: !!templateId && !!csrfToken && enabled !== false,
            retry: 0,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Sync accumulated and next_cursor from query data
    useEffect(() => {
        if (transactionsData) {
            const items = Array.isArray(transactionsData)
                ? transactionsData
                : (transactionsData.items ?? []);
            setAccumulatedTx(items);
            setNextTxCursor(transactionsData.next_cursor ?? '');
        }
    }, [transactionsData]);

    const hydratedRef = React.useRef(false);
    useEffect(() => {
        hydratedRef.current = false;
    }, [templateId]);
    useEffect(() => {
        if (onHydrated && enabled && !isQueryLoading && !hydratedRef.current && (transactionsData !== undefined || isQueryError)) {
            hydratedRef.current = true;
            onHydrated();
        }
    }, [enabled, isQueryLoading, onHydrated, transactionsData, isQueryError]);

    // Always show the button when there are items; enable when there's a next_cursor
    const showMoreTxButton = accumulatedTx.length > 0;
    const canShowMoreTx = !!nextTxCursor && !loadingMoreTx;

    const handleShowMoreTx = async () => {
        if (!canShowMoreTx) return;
        setLoadingMoreTx(true);
        try {
            const { items, next_cursor: next } = await fetchTransactionsPage(csrfToken, templateId, nextTxCursor);
            setAccumulatedTx((prev) => [...prev, ...items]);
            setNextTxCursor(next ?? '');
        } finally {
            setLoadingMoreTx(false);
        }
    };

    // Query for statements (first page; needed for payment forms). Only when transactions pane is in view.
    const { data: statementsData } = useQuery(
        ['statements', String(templateId), { cursor: '' }],
        listTemplateStatements(csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && enabled !== false,
            refetchOnWindowFocus: false,
        }
    );
    const statements = statementsData?.items ?? [];

    if (isQueryError) {
        console.error('Error fetching transactions:', isQueryError);
        return (
            <Typography sx={{color:theme.colors.text}}>
                Error fetching transactions, try refreshing{' '}
                <span
                    onClick={() => refetch()}
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        color: theme.colors.secondary
                    }}
                >
                    retry?
                </span>
            </Typography>
        );
    }

    const isOwner = () => {
        return authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase()
    }


    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 'none',
                    borderBottom: 'none',
                    mb: expandedView ? 0 : '40px',
                    '& .MuiTableContainer-root': {
                        borderBottom: 'none !important',
                        '&::after': {
                            display: 'none !important',
                        },
                    },
                }}
            >
                {/* Transactions List - Table Format - Always show table, even when empty */}
                <TableContainer 
                    component={Box}
                    sx={{
                        mb: 0,
                        pb: 0,
                        mt: 0,
                        pt: 0,
                        mr: 0,
                        ml: 0,
                        width: '100%',
                        borderRadius: expandedView ? 0 : 2,
                        backgroundColor: expandedView ? 'transparent' : (theme.colors.tableBackground || 'white'),
                        border: 'none !important',
                        borderBottom: 'none !important',
                        borderTop: 'none !important',
                        boxShadow: expandedView ? 'none' : (theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)'),
                        outline: 'none !important',
                        overflow: 'hidden',
                        '&::before': {
                            display: 'none !important',
                        },
                        '&::after': {
                            display: 'none !important',
                        },
                        '&.MuiPaper-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginBottom: '0 !important',
                            paddingBottom: '0 !important',
                            outline: 'none !important',
                            borderRadius: 2,
                            '&::before': {
                                display: 'none !important',
                            },
                            '&::after': {
                                display: 'none !important',
                            },
                        },
                        '& .MuiPaper-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginBottom: '0 !important',
                            paddingBottom: '0 !important',
                            outline: 'none !important',
                            borderRadius: 2,
                        },
                        '& .MuiTable-root': {
                            border: 'none !important',
                            borderBottom: 'none !important',
                            borderTop: 'none !important',
                            marginBottom: '0 !important',
                            '& .MuiTableBody-root': {
                                border: 'none !important',
                                borderBottom: 'none !important',
                                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                                    borderBottom: 'none !important',
                                },
                            },
                        },
                    }}
                >
                    <Table sx={{ 
                        border: 'none !important', 
                        borderBottom: 'none !important',
                        borderCollapse: 'collapse',
                        borderSpacing: 0,
                        width: '100%',
                        tableLayout: { xs: 'fixed', sm: 'auto' },
                    }}>
                        <TableHead>
                            <TableRow>
                                {/* Left spacer column - 56px spacing / header action */}
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, verticalAlign: 'middle' }}>
                                    {headerAction ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                            {headerAction}
                                        </Box>
                                    ) : null}
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, padding: '16px 16px 16px 0', fontSize: '1.75rem', py: 2 }}>Transactions</TableCell>
                                <TableCell sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }}></TableCell>
                                <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }}></TableCell>
                                {/* Right spacer column - 56px spacing */}
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Loading or dehydrated: show spinner in table body */}
                            {(hydrating || isQueryLoading) ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress sx={{ color: theme.colors?.primary || theme.colors?.brandSecondary }} />
                                    </TableCell>
                                </TableRow>
                            ) : accumulatedTx && accumulatedTx.length > 0 ? (
                                accumulatedTx.sort((a, b) => {
                                    return new Date(b.created_at) - new Date(a.created_at);
                                }).map((item) => (
                                    <React.Fragment key={`${item.id || item.created_at}+${item.user.email}`}>
                                    <TableRow 
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        {/* Left spacer column - 56px spacing */}
                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                                        <TableCell sx={{ 
                                            color: item.amount >= 0 ? (theme.colors.success || '#4caf50') : (theme.colors.error || '#f44336'), 
                                            fontWeight: 500, 
                                            padding: { xs: '16px 2px 16px 0', sm: '16px 16px 16px 0' }, 
                                            fontSize: '1.1rem',
                                            width: { xs: '60px', sm: 'auto' },
                                            maxWidth: { xs: '60px', sm: '120px' },
                                            minWidth: { xs: '60px', sm: 'auto' },
                                        }}>
                                            ${item.amount?.toFixed(2) || '0.00'}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: theme.colors.text, 
                                            pl: { xs: 0, sm: 0 }, 
                                            pr: { xs: 2, sm: 0 },
                                            fontSize: '1.1rem',
                                            width: { xs: 'auto', sm: 'auto' },
                                            minWidth: { xs: '100px', sm: 'auto' },
                                        }}>
                                            {FormatDate(item.created_at, dateFormatLevel)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ 
                                            color: theme.colors.text, 
                                            padding: '16px 0', 
                                            fontSize: '1.1rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: { xs: 'auto', sm: 'auto' },
                                            minWidth: { xs: '100px', sm: '150px' },
                                            maxWidth: { xs: '40%', sm: 'none' },
                                        }}>
                                            {item.user.email}
                                        </TableCell>
                                        {/* Right spacer column - 56px spacing */}
                                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}>
                                            {!item.verified_at && (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center',
                                                    width: '100%', 
                                                    height: '100%'
                                                }}>
                                                    <IconButton 
                                                        onClick={(e) => handleMenuClick(e, item.id)}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '8px',
                                                            minWidth: '40px',
                                                            minHeight: '40px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${theme.colors.text}` }} />
                                                        </Box>
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={menuAnchorEl}
                                                        open={Boolean(menuAnchorEl) && menuTransactionId === item.id}
                                                        onClose={handleMenuClose}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'right',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                    >
                                                        <MenuItem onClick={() => handlePaymentClick(item)}>Payment</MenuItem>
                                                    </Menu>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {/* Transaction Form Row - shows when editing this transaction */}
                                    {editingTransactionId === item.id && (
                                        <TransactionFormRow
                                            transaction={item}
                                            templateId={templateId}
                                            isOwner={isOwner()}
                                            statements={statements}
                                            transactions={accumulatedTx}
                                            onClose={() => setEditingTransactionId(null)}
                                            onSuccess={() => {
                                                queryClient.invalidateQueries(['transactions', String(templateId)]);
                                                queryClient.invalidateQueries(['templates']);
                                                setEditingTransactionId(null);
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                                ))
                            ) : null}
                            {showMoreTxButton && (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 1.5, borderBottom: 'none', verticalAlign: 'middle' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={!canShowMoreTx}
                                            onClick={handleShowMoreTx}
                                            sx={{
                                                color: theme?.colors?.brandSecondary,
                                                borderColor: theme?.colors?.brandSecondary,
                                                '&:hover': {
                                                    borderColor: theme?.colors?.brandSecondary,
                                                    backgroundColor: theme?.colors?.brandSecondary ? `${theme.colors.brandSecondary}14` : undefined,
                                                },
                                            }}
                                        >
                                            {loadingMoreTx ? 'Loading...' : 'Show more'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default TransactionsModel;
